import { useEffect, useState } from 'react';
import type { DebtorsReport, HomeSummary, MeResponse, ReceiptData, ReceiptList, StockListResponse } from '@kassa/shared';
import { api, ApiError } from '../api';
import { formatMoney, formatQty, hasPermission, UNIT_LABEL } from '../format';
import { bakuDay, bakuMonthStart } from '../pwa';
import { InstallApp } from './InstallApp';

// Sahib üçün mobil tətbiq (KT-94, dizayn: Mobil.dc.html). Yalnız oxu: Ana, Satışlar, Anbar, Borclular. Alt naviqasiya.
// İcazələr veb ilə eynidir (report.view, stock.view); məlumat keşlənmir — internet yoxdursa «Bağlantı yoxdur».

type Tab = 'home' | 'sales' | 'stock' | 'debtors';
type Range = 'today' | 'yesterday' | 'month';

interface Props {
  me: MeResponse;
  onLogout: () => void;
  onOpenAccount: () => void;
  onDesktop: () => void;
}

const RANGES: { key: Range; label: string }[] = [
  { key: 'today', label: 'Bu gün' },
  { key: 'yesterday', label: 'Dünən' },
  { key: 'month', label: 'Bu ay' },
];

function rangeQuery(r: Range): { from: string; to: string } {
  if (r === 'today') return { from: bakuDay(0), to: bakuDay(0) };
  if (r === 'yesterday') return { from: bakuDay(-1), to: bakuDay(-1) };
  return { from: bakuMonthStart(), to: bakuDay(0) };
}

function errText(err: unknown): string {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return 'Bağlantı yoxdur. İnternet qayıdanda yenidən cəhd edin.';
  return err instanceof ApiError ? err.message : 'Bağlantı yoxdur. İnternet qayıdanda yenidən cəhd edin.';
}

// Vaxt Bakı ilə (sahib xaricdə olsa da çek saatı kassadakı ilə eyni): dd.mm HH:MM
const BAKU_TIME = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Baku', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
function time(iso: string): string {
  const p = Object.fromEntries(BAKU_TIME.formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return `${p.day}.${p.month} ${p.hour}:${p.minute}`;
}

function useOnline(): boolean {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

function RangeChips({ value, onChange }: { value: Range; onChange: (r: Range) => void }) {
  return (
    <div className="m-chips" role="group" aria-label="Dövr">
      {RANGES.map((r) => (
        <button key={r.key} type="button" className={r.key === value ? 'm-chip m-chip-on' : 'm-chip'} aria-pressed={r.key === value} onClick={() => onChange(r.key)}>
          {r.label}
        </button>
      ))}
    </div>
  );
}

export function MobileApp({ me, onLogout, onOpenAccount, onDesktop }: Props) {
  const online = useOnline();
  const canReport = hasPermission(me, 'report.view');
  const canStock = hasPermission(me, 'stock.view');
  const tabs: { key: Tab; label: string; ok: boolean }[] = [
    { key: 'home', label: 'Ana', ok: canReport },
    { key: 'sales', label: 'Satışlar', ok: canReport },
    { key: 'stock', label: 'Anbar', ok: canStock },
    { key: 'debtors', label: 'Borclular', ok: canReport },
  ];
  const visible = tabs.filter((t) => t.ok);
  const [tab, setTab] = useState<Tab>(visible[0]?.key ?? 'home');

  return (
    <div className="m-shell">
      <header className="m-top">
        <div className="m-top-row">
          <span className="m-title">{me.tenant?.tenantName ?? 'Kassa'}</span>
          <button type="button" className="m-link" onClick={onOpenAccount}>
            Hesabım
          </button>
        </div>
        <span className="m-sub">
          {me.accountName}
          {me.outlet ? ` · ${me.outlet.name}` : ''}
        </span>
      </header>
      {!online && (
        <p className="m-offline" role="status">
          Bağlantı yoxdur — rəqəmlər internet qayıdanda yenilənəcək.
        </p>
      )}
      <main className="m-main">
        {visible.length === 0 && <p className="muted">Bu hesabın hesabat və anbar icazəsi yoxdur. Tam versiyadan istifadə edin.</p>}
        {tab === 'home' && canReport && <HomeTab />}
        {tab === 'sales' && canReport && <SalesTab />}
        {tab === 'stock' && canStock && <StockTab />}
        {tab === 'debtors' && canReport && <DebtorsTab />}
        <div className="m-footer">
          <InstallApp />
          <button type="button" className="m-link" onClick={onDesktop}>
            Tam versiya
          </button>
          <button type="button" className="m-link" onClick={onLogout}>
            Çıxış
          </button>
        </div>
      </main>
      {visible.length > 1 && (
        <nav className="m-nav" aria-label="Mobil bölmələr">
          {visible.map((t) => (
            <button key={t.key} type="button" className={t.key === tab ? 'm-nav-btn m-nav-on' : 'm-nav-btn'} aria-current={t.key === tab ? 'page' : undefined} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </nav>
      )}
    </div>
  );
}

// --- Ana ---------------------------------------------------------------------

function HomeTab() {
  const [range, setRange] = useState<Range>('today');
  const [data, setData] = useState<HomeSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setData(null); // dövr dəyişib: köhnə dövrün rəqəmi göstərilməsin
    api
      .reportHome(rangeQuery(range))
      .then((d) => !cancelled && setData(d))
      .catch((err: unknown) => !cancelled && setError(errText(err)));
    return () => {
      cancelled = true;
    };
  }, [range]);

  const rows: { label: string; value: number; tone?: string }[] = data
    ? [
        { label: 'Nağd', value: data.sales.cash },
        { label: 'Kart', value: data.sales.card },
        { label: 'Köçürmə', value: data.sales.transfer },
        { label: 'Nisyə', value: data.sales.credit, tone: 'warn' },
        { label: 'Qaytarma', value: data.sales.returns, tone: 'bad' },
        ...(data.finance
          ? [
              { label: 'Mənfəət', value: data.finance.grossProfit, tone: 'ok' },
              { label: 'Müştərilərin borcu', value: data.finance.customerDebt, tone: 'warn' },
              { label: 'Təchizatçıya borc', value: data.finance.supplierDebt, tone: 'bad' },
            ]
          : []),
      ]
    : [];

  return (
    <section aria-label="Ana">
      <RangeChips value={range} onChange={setRange} />
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      {!data && !error && <p className="muted">Yüklənir…</p>}
      {data && (
        <>
          <div className="m-hero">
            <span className="muted small">Xalis satış · {data.sales.count} sənəd</span>
            <strong className="m-hero-value">{formatMoney(data.sales.net)}</strong>
          </div>
          <ul className="m-rows">
            {rows.map((r) => (
              <li key={r.label} className="m-row">
                <span className={`m-dot m-dot-${r.tone ?? 'base'}`} aria-hidden="true" />
                <span>{r.label}</span>
                <strong>{formatMoney(r.value)}</strong>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

// --- Satışlar ------------------------------------------------------------------

function SalesTab() {
  const [range, setRange] = useState<Range>('today');
  const [q, setQ] = useState('');
  const [data, setData] = useState<ReceiptList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<{ id: string; data: ReceiptData | null; error: string | null } | null>(null);

  useEffect(() => setData(null), [range]);

  // Android «geri» düyməsi: çek açıq ikən tətbiqdən çıxmasın, siyahıya qayıtsın
  useEffect(() => {
    const onPop = () => setOpen(null);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    const t = setTimeout(() => {
      api
        .reportReceipts({ ...rangeQuery(range), q: q.trim() || undefined })
        .then((d) => !cancelled && setData(d))
        .catch((err: unknown) => !cancelled && setError(errText(err)));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [range, q]);

  const show = (id: string) => {
    window.history.pushState({ receipt: id }, '');
    setOpen({ id, data: null, error: null });
    api
      .receipt(id, false)
      .then((d) => setOpen((o) => (o?.id === id ? { id, data: d, error: null } : o)))
      .catch((err: unknown) => setOpen((o) => (o?.id === id ? { id, data: null, error: errText(err) } : o)));
  };

  if (open) {
    const d = open.data;
    return (
      <section aria-label="Çek">
        <button type="button" className="m-link" onClick={() => window.history.back()}>
          ← Satışlar
        </button>
        {open.error && (
          <p className="bad" role="alert">
            {open.error}
          </p>
        )}
        {!d && !open.error && <p className="muted">Yüklənir…</p>}
        {d && (
          <div className="m-card">
            <h2 className="m-h2">{d.number}</h2>
            <p className="muted small">
              {time(d.dateTime)} · {d.outlet} · {d.cashier}
            </p>
            <ul className="m-rows">
              {d.lines.map((l, i) => (
                <li key={i} className="m-line">
                  <span className="m-line-name">{l.name}</span>
                  <span className="muted small">
                    {formatQty(l.qty)} {UNIT_LABEL[l.unit] ?? l.unit} × {formatMoney(l.unitPrice)}
                  </span>
                  <strong>{formatMoney(l.amount)}</strong>
                </li>
              ))}
            </ul>
            <p className="m-total">
              Cəmi <strong>{formatMoney(d.total)}</strong>
            </p>
            <p className="muted small">
              Nağd {formatMoney(d.payments.cash)} · kart {formatMoney(d.payments.card)} · nisyə {formatMoney(d.payments.credit)}
            </p>
            {d.customer && <p className="muted small">Müştəri: {d.customer.name}</p>}
          </div>
        )}
      </section>
    );
  }

  return (
    <section aria-label="Satışlar">
      <RangeChips value={range} onChange={setRange} />
      <label className="field">
        Çek nömrəsi və ya müştəri
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Axtar" />
      </label>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      {!data && !error && <p className="muted">Yüklənir…</p>}
      {data && data.items.length === 0 && <p className="muted">Bu dövrdə satış yoxdur.</p>}
      {data && (
        <ul className="m-list">
          {data.items.map((r) => (
            <li key={r.id}>
              <button type="button" className="m-item" onClick={() => show(r.id)}>
                <span className="m-item-main">
                  <span className="mono">{r.displayNo}</span>
                  <strong className={r.kind === 'QAY' ? 'bad' : undefined}>
                    {r.kind === 'QAY' ? '−' : ''}
                    {formatMoney(r.total)}
                  </strong>
                </span>
                <span className="muted small">
                  {time(r.postedAt)} · {r.cashier ?? '—'}
                  {r.customer ? ` · ${r.customer}` : ''}
                </span>
                <span className="muted small">
                  {[r.cash ? `nağd ${formatMoney(r.cash)}` : null, r.card ? `kart ${formatMoney(r.card)}` : null, r.transfer ? `köçürmə ${formatMoney(r.transfer)}` : null, r.credit ? `nisyə ${formatMoney(r.credit)}` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {data?.truncated && <p className="muted small">Son 200 çek göstərilir — dövrü daraldın və ya axtarın.</p>}
    </section>
  );
}

// --- Anbar ---------------------------------------------------------------------

function StockTab() {
  const [q, setQ] = useState('');
  const [low, setLow] = useState(false);
  const [data, setData] = useState<StockListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setData(null), [low]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    const t = setTimeout(() => {
      api
        .stock({ q: q.trim() || undefined, low: low ? '1' : undefined, limit: 50 })
        .then((d) => !cancelled && setData(d))
        .catch((err: unknown) => !cancelled && setError(errText(err)));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, low]);

  return (
    <section aria-label="Anbar">
      <label className="field">
        Məhsul (ə, ş, ç olmadan da)
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Məs. corek" />
      </label>
      <div className="m-chips" role="group" aria-label="Süzgəc">
        <button type="button" className={!low ? 'm-chip m-chip-on' : 'm-chip'} aria-pressed={!low} onClick={() => setLow(false)}>
          Hamısı
        </button>
        <button type="button" className={low ? 'm-chip m-chip-on' : 'm-chip'} aria-pressed={low} onClick={() => setLow(true)}>
          Az qalanlar
        </button>
      </div>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      {!data && !error && <p className="muted">Yüklənir…</p>}
      {data && data.items.length === 0 && <p className="muted">{low ? 'Minimumdan aşağı mal yoxdur.' : 'Mal tapılmadı.'}</p>}
      {data && (
        <ul className="m-list">
          {data.items.map((r) => (
            <li key={r.productId} className="m-item m-item-static">
              <span className="m-item-main">
                <span>{r.name}</span>
                <strong className={r.low ? 'bad' : undefined}>
                  {formatQty(r.qtyFree)} {UNIT_LABEL[r.unit] ?? r.unit}
                </strong>
              </span>
              <span className="muted small">
                {r.category ?? 'Kateqoriyasız'}
                {r.minQty !== null ? ` · minimum ${formatQty(r.minQty)}` : ''}
                {r.qtyReserved > 0 ? ` · rezerv ${formatQty(r.qtyReserved)}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
      {data && data.total > data.items.length && <p className="muted small">{data.total} maldan {data.items.length}-i göstərilir — axtarın.</p>}
    </section>
  );
}

// --- Borclular -----------------------------------------------------------------

function DebtorsTab() {
  const [data, setData] = useState<DebtorsReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .reportDebtors()
      .then((d) => !cancelled && setData(d))
      .catch((err: unknown) => !cancelled && setError(errText(err)));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section aria-label="Borclular">
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      {!data && !error && <p className="muted">Yüklənir…</p>}
      {data && (
        <>
          <div className="m-hero">
            <span className="muted small">Müştərilərin borcu · {data.rows.length} müştəri</span>
            <strong className="m-hero-value">{formatMoney(data.totals.total)}</strong>
            {data.totals.d90 > 0 && <span className="bad small">90 gündən köhnə: {formatMoney(data.totals.d90)}</span>}
          </div>
          {data.rows.length === 0 && <p className="muted">Borclu müştəri yoxdur.</p>}
          <ul className="m-list">
            {data.rows.map((r) => (
              <li key={r.customerId} className="m-item m-item-static">
                <span className="m-item-main">
                  <span>{r.name}</span>
                  <strong>{formatMoney(r.total)}</strong>
                </span>
                {r.d90 + r.d61_90 > 0 && <span className="bad small">60 gündən köhnə: {formatMoney(r.d90 + r.d61_90)}</span>}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
